import React, { useEffect, useMemo, useState } from 'react';
import { PositionedModal } from '../../../common/components/PositionedModal';
import { Modal, Icon, Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import { StyledButton, StyledCancelButton } from 'common/components/button/StyledButton';
import { useToasts } from 'common/toast';
import { isEmployeeStatusActive } from '../../settings/employee-management/utils/EmployeeUtils';
import { useGetTeamDetailQuery, useGetOrganizationMembersQuery } from '../api/teamApi';
import { useApiPortalV1TeamsSetMembersCreateMutation } from '@/store/generated/portal/teams';
import { H2, H3, PageSection, Title } from '@/common/styles/page';

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid #efefef;
  cursor: pointer;
  min-height: 36px; /* compact rows */
  position: relative;
  &:hover {
    background: #f3f4f6; /* more prominent hover */
    transform: translateX(1px);
  }

  &:hover .member-name {
    font-weight: 700;
  }

  .member-badge,
  .available-badge,
  .row-badge {
    transition: opacity 160ms ease, transform 160ms ease;
  }

  .member-badge,
  .available-badge {
    opacity: 1;
  }

  &:hover .member-badge {
    opacity: 0;
  }

  .row-badge {
    opacity: 0;
  }

  &:hover .row-badge {
    opacity: 1;
  }

  .member-icon {
    opacity: 0;
    transition: opacity 160ms ease, transform 160ms ease;
    transform: translateY(-50%);
    position: absolute;
    top: 50%;
    right: 36px; /* arrow sits left of user icon */
    font-size: 16px;
    display: inline-block;
  }
  .member-icon.rotated { transform: translateY(-50%) rotate(180deg); }
  .member-icon.left { position: relative; top: 0; right: auto; transform: none; margin-right: 8px; }
  .member-icon.left.rotated { transform: rotate(180deg); }
  &:hover .member-icon { opacity: 1; }
  &.animate-out-right .member-icon, &.animate-out-left .member-icon, &.highlight .member-icon { opacity: 1; }
  .member-user-icon {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    margin: 0;
    color: #6b7280;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    transition: color 160ms ease, transform 160ms ease;
  }
  &:hover .member-user-icon { color: #111827; transform: translateY(-50%) translateX(2px); }
  &.animate-out-right { animation: slideOutRight 220ms ease forwards; }
  &.animate-out-left { animation: slideOutLeft 220ms ease forwards; }
  &.highlight { animation: highlightFlash 900ms ease forwards; }

  @keyframes slideOutRight {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(18px); opacity: 0; }
  }
  @keyframes slideOutLeft {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(-18px); opacity: 0; }
  }
  @keyframes highlightFlash {
    from { background: #fef3c7; }
    to { background: transparent; }
  }
`;

const MemberLabel = styled.div<{ badgeSide?: 'left' | 'right' }>`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  min-width: 0; /* allow truncation */
  flex: 1;
  padding-left: ${props => (props.badgeSide === 'left' ? '34px' : '0')};
  padding-right: ${props => (props.badgeSide === 'right' ? '34px' : '0')};
`;

const TeamTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TeamColorBadge = styled.span<{ color?: string }>`
  width: 24px;
  height: 24px;
  border-radius: 35%;
  background: ${props => props.color || '#9ca3af'};
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
`;


const MemberEmail = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1;
  opacity: 0.95;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: auto;
  text-align: right;
`;

const RowBadge = styled.span<{ variant: 'add' | 'remove' }>`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 35%;
  font-size: 14px;
  font-weight: 700;
  z-index: 2;
  color: ${props => (props.variant === 'add' ? '#ffffff' : '#ffffff')};
  background: ${props => (props.variant === 'add' ? '#2d70e2' : '#bbbbbb')};
  border: 1px solid ${props => (props.variant === 'add' ? 'transparent' : 'transparent')};
`;

const MemberBadge = styled.span<{ color?: string }>`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 35%;
  background: ${props => props.color || '#9ca3af'};
  border: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 1;
`;

const ListContainer = styled.div`
  flex: 1;
  min-height: 360px;
  max-height: 520px;
  height: 100%;
  overflow: auto;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const LeaderBadge = styled.span`
  background: #ecfdf5;
  color: #065f46;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 8px; 
  border-radius: 5px;
  line-height: 1.5;
  border: 1px solid #b5d4ca;
`;
interface TeamMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  onSuccess?: () => void;
}

export const TeamMembersModal: React.FC<TeamMembersModalProps> = ({ isOpen, onClose, teamId, onSuccess }) => {
  const { data: orgMembersResponse } = useGetOrganizationMembersQuery(undefined, {
    skip: !isOpen,
    refetchOnMountOrArgChange: true,
  });
  const orgMembers = orgMembersResponse?.results || [];
  const activeOrgMembers = useMemo(
    () => orgMembers.filter((member) => member.status == null || isEmployeeStatusActive(member.status)),
    [orgMembers]
  );

  const { data: teamDetail, refetch: refetchTeamDetail } = useGetTeamDetailQuery(teamId, { skip: !isOpen, refetchOnMountOrArgChange: true });
  const [setMembers, { isLoading }] = useApiPortalV1TeamsSetMembersCreateMutation();
  const { addToast } = useToasts();

  const leaderId = teamDetail?.leader_id ?? null;

  // Build initial selection directly from the API payload. All returned members are active
  const initialSelected = useMemo(() => {
    if (!teamDetail) return new Set<number>();
    return new Set(teamDetail.members.map((m: any) => m.employee_id ?? m.employee?.id));
  }, [teamDetail]);

  // Members currently assigned to the team (local state)
  const [membersSet, setMembersSet] = useState<Set<number>>(new Set());
  // Local filters for each column
  const [leftFilter, setLeftFilter] = useState<string>('');
  const [rightFilter, setRightFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const [error, setError] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Animation / transient state
  const [animatingAddIds, setAnimatingAddIds] = useState<Set<number>>(new Set());
  const [animatingRemoveIds, setAnimatingRemoveIds] = useState<Set<number>>(new Set());
  const [highlightIds, setHighlightIds] = useState<Set<number>>(new Set());
  const timersRef = React.useRef<number[]>([]);
  const ANIM_MS = 220;
  const HIGHLIGHT_MS = 850;

  // When modal opens or the selected team changes, reset to a fresh state immediately
  // This prevents showing the previous team's members while a new team is loading
  useEffect(() => {
    if (!isOpen) return;

    // Reset UI eagerly so there's no flash of previous data while fetching
    setMembersSet(new Set());
    setLeftFilter('');
    setRightFilter('');
    setCategoryFilter('');
    setError('');
    setIsInitialized(false);

    // If we already have teamDetail (prefetch hit), initialize from it immediately
    if (!teamDetail) {
      return;
    }

    // Initialize state when we have fresh team detail
    const initial = new Set(initialSelected);
    setMembersSet(initial);
    setLeftFilter('');
    setRightFilter('');
    setError('');
    setIsInitialized(true);

    return () => {
      // clear any pending timers when modal closes/unmounts
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];
    };
  }, [isOpen, teamId, teamDetail, initialSelected]);

  // Ensure we clear state when the modal is closed (this runs synchronously when isOpen flips false)
  useEffect(() => {
    if (!isOpen) {
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];
      setMembersSet(new Set());
      setLeftFilter('');
      setRightFilter('');
      setCategoryFilter('');
      setError('');
      setIsInitialized(false);
    }
  }, [isOpen]);

  // Derived lists
  const availableMembers = useMemo(
    () => activeOrgMembers
      .filter((m) => m.id !== leaderId && !membersSet.has(m.id))
      .filter((m) => categoryFilter === 'unassigned' ? !m.assignedToTeam : true),
    [activeOrgMembers, membersSet, leaderId, categoryFilter]
  );

  const categoryOptions = useMemo(() => {
    const categories = Array.from(
      new Set(
        availableMembers
          .map((m) => (m.primaryCategory || '').trim() || 'Uncategorized')
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

    return [
      { key: 'all', value: '', text: 'All Members' },
      { key: 'unassigned', value: 'unassigned', text: '[Unassigned Members]' },
      ...categories.map(category => ({ key: category, value: category, text: category })),
    ];
  }, [availableMembers]);

  const filteredLeft = useMemo(() => {
    const q = leftFilter.trim().toLowerCase();

    const list = Array.from(membersSet)
      .map((id) => {
        const found = orgMembers.find((m) => m.id === id);
        if (found && (!found.status || isEmployeeStatusActive(found.status))) {
          return found;
        }
        if (found && found.status != null && !isEmployeeStatusActive(found.status)) {
          return null;
        }
        const raw = teamDetail?.members?.find((m: any) => (m.employee_id ?? m.employee?.id) === id);
        if (!raw) return null;
        const r: any = raw;
        return {
          id,
          firstName: r.employee_name || '',
          lastName: '',
          email: r.employee_email || r.email || '',
          assignedToTeam: true,
        } as any;
      })
      .filter(Boolean) as typeof orgMembers;

    const filtered = list.filter(m => {
      if (!q) return true;
      const firstName = (m!.firstName || '').toLowerCase();
      const lastName = (m!.lastName || '').toLowerCase();
      return firstName.startsWith(q) || lastName.startsWith(q);
    });

    return filtered.sort((a, b) => {
      const lastA = (a!.lastName || '').toLowerCase();
      const lastB = (b!.lastName || '').toLowerCase();
      const firstA = (a!.firstName || '').toLowerCase();
      const firstB = (b!.firstName || '').toLowerCase();
      const emailA = (a!.email || '').toLowerCase();
      const emailB = (b!.email || '').toLowerCase();

      const lastCompare = lastA.localeCompare(lastB);
      if (lastCompare !== 0) return lastCompare;

      const firstCompare = firstA.localeCompare(firstB);
      if (firstCompare !== 0) return firstCompare;

      return emailA.localeCompare(emailB);
    });
  }, [membersSet, orgMembers, leaderId, leftFilter, teamDetail]);

  const filteredRight = useMemo(() => {
    const q = rightFilter.trim().toLowerCase();
    const filtered = availableMembers.filter(m => {
      if (categoryFilter && categoryFilter !== 'unassigned' && ((m.primaryCategory || '').trim() || 'Uncategorized') !== categoryFilter) return false;
      if (!q) return true;
      const firstName = (m.firstName || '').toLowerCase();
      const lastName = (m.lastName || '').toLowerCase();
      return firstName.startsWith(q) || lastName.startsWith(q);
    });

    return filtered.sort((a, b) => {
      const lastA = (a.lastName || '').toLowerCase();
      const lastB = (b.lastName || '').toLowerCase();
      const firstA = (a.firstName || '').toLowerCase();
      const firstB = (b.firstName || '').toLowerCase();
      const emailA = (a.email || '').toLowerCase();
      const emailB = (b.email || '').toLowerCase();

      const lastCompare = lastA.localeCompare(lastB);
      if (lastCompare !== 0) return lastCompare;

      const firstCompare = firstA.localeCompare(firstB);
      if (firstCompare !== 0) return firstCompare;

      return emailA.localeCompare(emailB);
    });
  }, [availableMembers, rightFilter, categoryFilter]);

  const handleAddAllFiltered = () => {
    if (filteredRight.length === 0) return;
    setMembersSet(prev => {
      const next = new Set(prev);
      filteredRight.forEach(m => next.add(m.id));
      return next;
    });
    setRightFilter('');
  };

  const handleRemoveAllFiltered = () => {
    if (filteredLeft.length === 0) return;
    setMembersSet(prev => {
      const next = new Set(prev);
      filteredLeft.forEach(m => next.delete(m.id));
      return next;
    });
    setLeftFilter('');
  };

  const addMemberFromBadge = (id: number) => {
    setRightFilter('');
    addMember(id);
  };

  const removeMemberFromBadge = (id: number) => {
    setLeftFilter('');
    removeMember(id);
  };

  const leaderMember = useMemo(() => orgMembers.find(m => m.id === leaderId), [orgMembers, leaderId]);
  const teamColor = teamDetail?.color || '#9ca3af';

  // Display counts, factoring in pending animations so counts update immediately when user clicks
  const membersBaseCount = Array.from(membersSet).filter(id => id !== leaderId).length;
  const pendingAddsCount = animatingAddIds.size;
  const pendingRemovesCount = animatingRemoveIds.size;
  const membersCountDisplay = membersBaseCount + pendingAddsCount - pendingRemovesCount;
  const availableCountDisplay = availableMembers.length - pendingAddsCount + pendingRemovesCount;

  const addMember = (id: number) => {
    if (animatingAddIds.has(id) || animatingRemoveIds.has(id)) return; // already animating
    setAnimatingAddIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    const t = window.setTimeout(() => {
      // finish animating out from available; add to members and flash highlight
      setAnimatingAddIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setMembersSet(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setHighlightIds(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const t2 = window.setTimeout(() => {
        setHighlightIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, HIGHLIGHT_MS);
      timersRef.current.push(t2);
    }, ANIM_MS);

    timersRef.current.push(t);
  };

  const removeMember = (id: number) => {
    // Allow removing a leader id from the team member list if it was added by accident.
    // This only updates employee-team membership; leader_id remains unchanged on the team.
    if (animatingAddIds.has(id) || animatingRemoveIds.has(id)) return; // already animating
    setAnimatingRemoveIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    const t = window.setTimeout(() => {
      setAnimatingRemoveIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setMembersSet(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setHighlightIds(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      const t2 = window.setTimeout(() => {
        setHighlightIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, HIGHLIGHT_MS);
      timersRef.current.push(t2);
    }, ANIM_MS);

    timersRef.current.push(t);
  };

  const handleUpdate = async () => {
    setError('');
    try {
      const employee_ids = Array.from(membersSet);

      // compute added/removed locally so we have a fallback
      const addedIdsLocal = employee_ids.filter(id => !initialSelected.has(id));
      const removedIdsLocal = Array.from(initialSelected).filter(id => !membersSet.has(id));

      const res: any = await setMembers({ id: teamId, body: { employee_ids } }).unwrap();

      // refresh team detail and any member lists
      refetchTeamDetail();

      // Prefer server-provided result when available
      const result = res?.result;
      const addedCount = (result && Array.isArray(result.added_ids)) ? result.added_ids.length : addedIdsLocal.length;
      const removedCount = (result && Array.isArray(result.removed_ids)) ? result.removed_ids.length : removedIdsLocal.length;

      // Show a concise success summary using toast
      addToast(`Members updated. Added: ${addedCount}. Removed: ${removedCount}.`, { appearance: 'success', autoDismiss: true });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to update members', err);
      // Robust error handling for cases where the server returns non-JSON (e.g., HTML error pages)
      const data = err?.data;
      let msg = 'Failed to update team members. Please try again.';

      if (typeof data === 'string') {
        // sanitize HTML response to a short plaintext snippet
        const withoutScripts = data.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
        const stripped = withoutScripts.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        const short = stripped ? (stripped.length > 300 ? `${stripped.slice(0, 300)}...` : stripped) : 'Server returned an unexpected response.';
        msg = `Server error: ${short}`;
      } else if (data?.detail) {
        msg = data.detail;
      } else if (data?.employee_ids) {
        msg = data.employee_ids[0];
      } else if (err?.status === 'PARSING_ERROR' || (err?.originalStatus && err.originalStatus >= 500)) {
        msg = 'Server error (unexpected response). Please try again or contact support.';
      }

      setError(msg);
    }
  };



  return (
    <PositionedModal open={!!isOpen} onClose={onClose} size="large">
      <Modal.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Title>
            <TeamTitleRow>
              <TeamColorBadge color={teamDetail?.color || '#9ca3af'} />
              {isInitialized && teamDetail?.name ? teamDetail.name : ''}
            </TeamTitleRow>
          </Title>
          {leaderMember && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <H2>{(leaderMember.firstName || leaderMember.lastName) ? `${leaderMember.lastName}${leaderMember.firstName ? `, ${leaderMember.firstName}` : ''}` : leaderMember.email}</H2>
              <LeaderBadge aria-label="Leader badge">Leader</LeaderBadge>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Content style={{ minWidth: 0, width: '100%', maxWidth: 'calc(100vw - 80px)', minHeight: 540, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>


        {!isInitialized ? (
          // Skeleton placeholders to keep layout stable while fetching fresh team detail
          <div style={{ display: 'flex', gap: 12, marginTop: 8, minHeight: 420, width: '100%' }}>
            <div style={{ flex: 1, order: 1 }}>
              <div style={{ height: 24, width: 140, background: '#f3f4f6', borderRadius: 4, marginBottom: 8 }} />
              <div style={{ border: '1px solid #e6e6e6', borderRadius: 6, padding: 12, minHeight: 240 }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} style={{ height: 36, background: '#f3f4f6', borderRadius: 6, marginBottom: 8 }} />
                ))}
              </div>
            </div>

            <div style={{ flex: 2, order: 0 }}>
              <div style={{ height: 24, width: 120, background: '#f3f4f6', borderRadius: 4, marginBottom: 8 }} />
              <div style={{ border: '1px solid #e6e6e6', borderRadius: 6, padding: 12, minHeight: 240 }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 8px', marginBottom: 8 }}>
                    <div style={{ height: 28, width: 220, background: '#f3f4f6', borderRadius: 6 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, marginTop: 8, minHeight: 440 }}>
            <div style={{ flex: 1, order: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <H2>Available</H2>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Dropdown
                      compact
                      selection
                      options={categoryOptions}
                      value={categoryFilter}
                      onChange={(_, { value }) => setCategoryFilter(value as string)}
                      placeholder="All members"
                      style={{ minWidth: 180, height: 38 }}
                    />
                    <input
                      placeholder="Filter by name..."
                      value={rightFilter}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRightFilter(e.target.value)}
                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', minWidth: 160, height: 38 }}
                    />
                  </div>
                </div>

              <ListContainer>
                {filteredRight.length === 0 ? (
                  <div style={{ padding: 12, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>No available members.</div>
                ) : (
                  filteredRight.map(m => {
                    const rowClass = animatingAddIds.has(m.id) ? 'animate-out-right' : '';
                    const iconClass = animatingAddIds.has(m.id) ? 'animate-out-right' : (highlightIds.has(m.id) ? 'highlight' : '');
                    return (
                      <MemberRow
                        key={m.id}
                        className={rowClass}
                        onClick={() => addMember(m.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addMember(m.id); } }}
                      >
                        <MemberLabel badgeSide="left">
                          <MemberBadge className="available-badge" color="#d1d5db">
                            {m.assignedToTeam && <Icon name="check" style={{ color: '#ffffff', fontSize: '12px', transform: 'translate(2px, -2px)' }} />}
                          </MemberBadge>
                          <H3 style={{ color: m.assignedToTeam ? '#9ca3af' : undefined }}>
                            {(m.firstName || m.lastName) ? `${m.lastName}${m.firstName ? `, ${m.firstName}` : ''}` : m.email}
                          </H3>
                          <MemberEmail>{m.primaryCategory || ''}</MemberEmail>
                          <RowBadge
                            className="row-badge"
                            variant="add"
                            onClick={(e) => { e.stopPropagation(); addMemberFromBadge(m.id); }}
                          >&gt;</RowBadge>
                        </MemberLabel>
                      </MemberRow>
                    );
                  })
                )}
              </ListContainer>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 10, width: 64 }}>
              <StyledButton
                label="≫"
                width="34px"
                height="34px"
                color="blue"
                onClick={handleAddAllFiltered}
                disabled={filteredRight.length === 0}
                aria-label="Add all filtered available members"
              />
              <StyledButton
                label="≪"
                width="34px"
                height="34px"
                color="gray"
                onClick={handleRemoveAllFiltered}
                disabled={filteredLeft.length === 0}
                aria-label="Remove all filtered team members"
              />
            </div>

            <div style={{ flex: 1, order: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <H2>Team Members</H2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    placeholder="Filter by name..."
                    value={leftFilter}
                    onChange={(e) => setLeftFilter(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', minWidth: 160, height: 38 }}
                  />
                </div>
              </div>

              <ListContainer>
                {Array.from(membersSet).length === 0 ? (
                  <div style={{ padding: 12, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>No team members.</div>
                ) : (
                  filteredLeft
                    .map(m => {
                      const isLeader = leaderId != null && m.id === leaderId;
                      const rowClass = animatingRemoveIds.has(m.id) ? 'animate-out-left' : '';
                      const iconClass = animatingRemoveIds.has(m.id) ? 'animate-out-left' : (highlightIds.has(m.id) ? 'highlight' : '');
                      return (
                        <MemberRow
                          key={m.id}
                          className={rowClass}
                          onClick={() => removeMember(m.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeMember(m.id); } }}
                          style={{ cursor: 'pointer' }}
                        >
                          <MemberLabel badgeSide="left">
                            <MemberBadge className="member-badge" color={teamColor} />
                            <RowBadge
                              className="row-badge"
                              variant="remove"
                              onClick={(e) => { e.stopPropagation(); removeMemberFromBadge(m.id); }}
                            >&lt;</RowBadge>
                            <H3>
                              {(m.firstName || m.lastName) ? `${m.lastName}${m.firstName ? `, ${m.firstName}` : ''}` : m.email}
                              {isLeader && <span style={{ marginLeft: 8, fontSize: 11, color: '#065f46', fontWeight: 700 }}>Leader</span>}
                            </H3>
                            <MemberEmail>{m.primaryCategory || ''}</MemberEmail>
                          </MemberLabel>
                        </MemberRow>
                      );
                    })
                )}
              </ListContainer>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#DC2626', marginTop: 8 }}>{error}</div>}
      </Modal.Content>

      <Modal.Actions>
        <PageSection style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 0, paddingBottom: 16, paddingRight: 24, paddingLeft: 24 }}  >
          <StyledCancelButton onClick={onClose} disabled={isLoading} />
          <StyledButton label={isLoading ? 'Updating...' : 'Update'} color="blue" width="120px" onClick={handleUpdate} disabled={isLoading} />
        </PageSection>
      </Modal.Actions>
    </PositionedModal>
  );
};

export default TeamMembersModal;